<script lang="ts" setup>
import type { FooterSettings } from '@convatec/180medical-website/types/app'
import type { LinkItem } from '@convatec/180medical-website/types/button'

const { footerSettings } = defineProps<Props>()

const appSettings = useAppSettingsStore()

const { openChat } = useTalkdeskChat()

interface Props {
  footerSettings: FooterSettings
}

const formatPhone = (phone: string) => phone.replace(/\D/g, '')
const logoSrc = '/images/180-logo.png'

function linkLabel(link: LinkItem): string {
  if (link.title) {
    return link.title
  }
  if (link.icon?.imageAltText) {
    return link.icon.imageAltText
  }
  if (link.text) {
    return link.text
  }
  try {
    return new URL(link.url).hostname.replace(/^www\./, '')
  }
  catch {
    return 'External link'
  }
}

function handleChatClick() {
  openChat()
}
</script>

<template>
  <div class="border-solid border-t border-med-light-aqua bg-white pt-20 pb-[60px] md:pt-[50px]">
    <div class="container-fluid">
      <div class="sm:flex flex-wrap">
        <div class="w-full md:w-2/4 xl:w-1/4 shrink-0">
          <div class="footer__contact">
            <div class="footer__logo">
              <NuxtImg
                :src="logoSrc"
                alt="180 Medical"
                width="223"
                height="121"
                loading="lazy"
              />
            </div>
            <UButton
              :to="useCmsUrl(footerSettings?.contactButton?.url)"
              :locale="false"
              variant="solid"
              color="primary"
              class="mb-5"
            >
              {{ footerSettings?.contactButton?.text }}
            </UButton>
            <ul class="footer__social">
              <li
                v-for="link in footerSettings.socialLinks"
                :key="link.url"
              >
                <ULink
                  :to="link.url"
                  :target="link.target || undefined"
                  :aria-label="linkLabel(link)"
                >
                  <NuxtImg
                    provider="fastlyAprimo"
                    class="max-w-[35px] max-h-[35px] object-contain"
                    :src="link.icon?.imageUrl"
                    :alt="link.icon?.imageAltText || ''"
                    :width="link.icon?.width ?? 35"
                    :height="link.icon?.height ?? 35"
                    loading="lazy"
                  />
                </ULink>
              </li>
            </ul>
          </div>
        </div>
        <div class="sm:w-7/12 md:w-2/4 xl:w-5/12 shrink-0">
          <div
            v-for="(section, index) in footerSettings?.contactSections"
            :key="index"
            class="footer__icon-item mobile-set-width"
          >
            <NuxtImg
              provider="fastlyAprimo"
              :src="section.icon?.imageUrl"
              :alt="section.icon?.imageAltText || ''"
              class="footer__icon-item__icon aspect-[1/1] max-w-[44px]"
              loading="lazy"
              :width="section.icon?.width ?? 44"
              :height="section.icon?.height ?? 44"
            />
            <div>
              <template
                v-for="(contact, i) in section.contacts"
                :key="i"
              >
                <template v-if="!contact.link">
                  <strong>
                    {{ contact.label }}:
                  </strong>
                  <div
                    class="inline"
                    v-html="`<a href='tel:${formatPhone(contact.text)}' class='underline'>${contact.text}</a>`"
                  />
                </template>
                <template v-else-if="appSettings?.talkdeskSettings?.enableChat">
                  {{ contact.label }}
                  <button
                    class="underline cursor-pointer"
                    @click="handleChatClick"
                  >
                    {{ contact.link.text }}
                  </button>
                </template>
                <template v-else>
                  {{ contact.label }}
                  <a
                    :href="contact.link.url"
                    class="underline"
                  >
                    {{ contact.link.text }}
                  </a>
                </template>
                <br>
              </template>
            </div>
          </div>
        </div>
        <div class="sm:w-5/12 md:w-2/4 xl:w-1/6 shrink-0">
          <div class="h2 footer__link-title mobile-set-width">
            {{ footerSettings.quickLinks?.title }}
          </div>
          <ul class="footer__link-list mobile-set-width">
            <li
              v-for="(link, index) in footerSettings.quickLinks?.links"
              :key="index"
            >
              <ULink
                :to="useCmsUrl(link.url)"
                :locale="false"
                :target="link.target || undefined"
                class="hover:underline"
              >
                {{ link.text }}
              </ULink>
            </li>
          </ul>
        </div>
        <div class="md:w-2/4 xl:w-1/6 shrink-0 prose">
          <div class="h2 footer__link-title">
            {{ footerSettings.appLinks?.title }}
          </div>
          <p>{{ footerSettings.appLinks?.description }}</p>
          <div class="footer__app-links not-prose">
            <ULink
              v-for="(app, index) in footerSettings.appLinks?.links"
              :key="index"
              :to="app.url"
              :target="app.target || undefined"
              :aria-label="linkLabel(app)"
            >
              <NuxtImg
                provider="fastlyAprimo"
                :src="app.icon?.imageUrl"
                :alt="app.icon?.imageAltText || ''"
                class="aspect-[150/44]"
                loading="lazy"
                :width="app.icon?.width ?? 150"
                :height="app.icon?.height ?? 44"
              />
            </ULink>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="bg-med-deep-navy border-solid border-t-5 border-med-aqua lg:border-0">
    <div class="container-fluid">
      <div class="sub-footer__row">
        <ul class="sub-footer__links text-white ">
          <li>{{ footerSettings.policyLinks?.title }}</li>
          <li
            v-for="(link, index) in footerSettings.policyLinks?.links"
            :key="index"
          >
            <ULink
              :to="useCmsUrl(link.url)"
              :locale="false"
              :target="link.target || undefined"
              class="visited:text-white active:text-white hover:underline"
            >
              {{ link.text }}
            </ULink>
          </li>
          <li>
            <button
              type="button"
              class="cursor-pointer hover:underline"
              @click="openCookiePreferences"
            >
              Privacy Choices
            </button>
          </li>
        </ul>
        <div class="sub-footer__icons">
          <ULink
            v-for="(link, index) in footerSettings.accreditedLinks?.links"
            :key="index"
            :to="link.url"
            :target="link.target || undefined"
            :aria-label="linkLabel(link)"
          >
            <NuxtImg
              provider="fastlyAprimo"
              :src="link.icon?.imageUrl"
              :alt="link.icon?.imageAltText || ''"
              loading="lazy"
              :width="link.icon?.width ?? 200"
              :height="link.icon?.height ?? 70"
              quality="80"
            />
          </ULink>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.footer__contact {
  text-align: center;
  width: 100%;
  @media (min-width: 768px) {
    text-align: left;
  }
  @media (min-width: 1200px) {
    text-align: center;
  }
}
.footer__logo {
  text-align: center;
  display: block;
  margin-bottom: 40px;
  img {
    width: 190px;
    height: auto;
    margin-left: auto;
    margin-right: auto;
  }
  @media (min-width: 768px) {
    margin-bottom: 35px;
    img {
      width: 223px;
      margin-left: 0px;
    }
  }
  @media (min-width: 1200px) {
    img {
      margin-left: auto;
    }
  }
}
.footer__btn {
  display: block;
  width: 275px;
  max-width: 100%;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 40px;
  @media (min-width: 768px) {
    margin-left: 0;
    margin-bottom: 35px;
  }
  @media (min-width: 1200px) {
    margin-left: auto;
    width: 180px;
    padding: 6px 12px;
    min-height: 35px;
  }
}
.footer__social {
  width: 100%;
  list-style: none;
  padding-left: 0;
  margin-bottom: 40px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 10px 45px;
  @media (min-width: 768px) {
    justify-content: flex-start;
  }
  @media (min-width: 1200px) {
    margin-bottom: 0;
    justify-content: center;
    gap: 10px 30px;
  }
}
.footer__icon-item {
  color: var(--color-med-navy);
  margin-bottom: 40px;
  p {
    line-height: 1.875;
    text-wrap: pretty;
  }
  a,
  button {
    color: var(--color-med-dark-gray);
  }
  > * {
    &:last-child {
      margin-bottom: 0;
    }
  }
  @media (min-width: 768px) {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 20px;
  }
  @media (min-width: 1200px) {
    padding-left: 40px;
    &:last-child {
      margin-bottom: 0;
    }
  }
}
.footer__icon-item__icon {
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
}
.footer__link-list {
  list-style: none;
  padding-left: 0;
  margin-bottom: 40px;
  li {
    margin-bottom: 20px;
    &:last-child {
      margin-bottom: 0;
    }

    a {
      color: var(--color-med-deep-navy);
    }
  }
  @media (min-width: 1200px) {
    margin-bottom: 0;
  }
}
.footer__link-title {
  font-size: 1.125rem;
  font-family: var(--font-body);
  font-weight: 700;
  margin-bottom: 20px;
}
.footer__app-links {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 20px;
  margin-top: 40px;

  img {
    width: 150px;
    height: 44px;
    object-fit: contain;
  }

  @media (min-width: 768px) {
    justify-content: flex-start;
    gap: 10px;
    margin-top: 0;
  }
}

.sub-footer__row {
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.78;
  padding: 40px 0px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 26px;
  @media (min-width: 992px) {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    gap: 30px;
  }
}
.sub-footer__links {
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1.78;
  text-wrap: pretty;
  list-style: none;
  padding-left: 0;
  margin-bottom: 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  li {
    position: relative;
    display: inline-block;
    padding-right: 10px;
    &::after {
      content: '|';
      display: inline-block;
      padding-left: 10px;
    }
    &:last-child {
      padding-right: 0px;
      &::after {
        display: none;
      }
    }

    a {
      color: white;
    }
  }
}
.sub-footer__icons {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  width: 100%;
  > * {
    flex: 0 1 auto;
    position: relative;
    display: inline-block;
    max-width: calc(50% - 10px);
    height: 70px;
  }
  img {
    object-fit: contain;
    width: auto;
    max-width: 180px;
    height: 70px;
  }
  @media (min-width: 768px) {
    img {
      max-width: 220px;
    }
  }
  @media (min-width: 992px) {
    flex-shrink: 0;
    max-width: 50%;
    flex-wrap: wrap;
    justify-content: flex-end;
    width: auto;
    > * {
      max-width: 100%;
    }
    img {
      max-width: 290px;
    }
  }
}
</style>
